import { addQueryString } from 'utils/addQueryString'
import { ErrorResponse } from '../types'
import { request } from '../utils'
import { PATH } from './constants'
import {
  AllCarGroupsResponse,
  AllCarsResponse,
  AllExtraTypesResponse,
  CitiesResponse,
  CountriesResponse,
  CurrenciesResponse,
  LocationsResponse,
  ListResponse,
  AllCarTypesResponse,
  ListItemType,
  TypeOfLocation,
} from './types'
import { EnvironmentVariablesService } from 'utils/env'

class Api {
  public baseUrl = EnvironmentVariablesService.getEnv('REACT_APP_API_URL') || ''

  async getCurrencies() {
    return request<CurrenciesResponse, ErrorResponse>(
      this.baseUrl,
      PATH.CURRENCIES,
      { method: 'GET' }
    )
  }

  async getCountries(
    companyId = '',
    useStatesHierarchy = false,
    useBlackList = true
  ) {
    return request<CountriesResponse, ErrorResponse>(
      this.baseUrl,
      addQueryString(PATH.COUNTRIES, {
        companyId,
        useStatesHierarchy,
        useBlackList,
      }),
      { method: 'GET' }
    )
  }

  async getAllCompanyHirePoints(companyId: string, onlyActive = false) {
    return request<LocationsResponse, ErrorResponse>(
      this.baseUrl,
      addQueryString(PATH.ALL_HIRE_POINTS, { companyId, onlyActive }),
      { method: 'GET' }
    )
  }

  async getAllCarGroups(companyId = '') {
    return request<AllCarGroupsResponse, ErrorResponse>(
      this.baseUrl,
      `${PATH.ALL_CAR_GROUPS}/${companyId}`,
      { method: 'GET' }
    )
  }

  async getAllCars(companyId: string) {
    return request<AllCarsResponse, ErrorResponse>(
      this.baseUrl,
      addQueryString(PATH.ALL_CARS, { companyId }),
      { method: 'GET' }
    )
  }

  async getCities(countryId: number, companyId = '') {
    return request<CitiesResponse, ErrorResponse>(
      this.baseUrl,
      addQueryString(PATH.CITIES, { countryId, companyId }),
      { method: 'GET' }
    )
  }

  async getLocations({
    cityId = '',
    companyId = '',
    cities = [],
  }: {
    cityId?: string
    companyId?: string
    cities?: string[]
  }) {
    return request<LocationsResponse, ErrorResponse>(
      this.baseUrl,
      addQueryString(PATH.LOCATIONS, {
        cityId,
        companyId,
        cities,
      })
    )
  }

  async getAvailableLocations({
    cityId = '',
    companyId,
    currentLocationId = '',
    pendingLocationId = '',
    pendingCityId = '',
    typeOfLocation,
  }: {
    cityId?: string
    companyId: string
    currentLocationId?: string
    pendingCityId?: string
    pendingLocationId?: string
    typeOfLocation?: TypeOfLocation
  }) {
    return request<LocationsResponse, ErrorResponse>(
      this.baseUrl,
      addQueryString(PATH.AVAILABLE_LOCATIONS, {
        companyId,
        cityId: pendingCityId ? '' : cityId,
        includeLocationId: pendingLocationId ? '' : currentLocationId,
        pendingCityId,
        includePendingLocationId: pendingLocationId,
        typeOfLocation: typeOfLocation ? typeOfLocation : '',
      })
    )
  }

  async getAllExtraTypes(companyId = '', isSpecificExtraOnly = true) {
    return request<AllExtraTypesResponse, ErrorResponse>(
      this.baseUrl,
      addQueryString(PATH.ALL_EXTRAS, { companyId, isSpecificExtraOnly }),
      { method: 'GET' }
    )
  }

  async getAvailableCountries(
    currentLocationId = '',
    useStatesHierarchy = true,
    useBlackList = true
  ) {
    return request<CountriesResponse, ErrorResponse>(
      this.baseUrl,
      addQueryString(PATH.COUNTRIES, {
        includeLocationId: currentLocationId,
        useStatesHierarchy,
        useBlackList,
      })
    )
  }

  async getCarSeats() {
    return request<ListResponse, ErrorResponse>(this.baseUrl, PATH.SEATS)
  }

  async getCarDoors() {
    return request<ListResponse, ErrorResponse>(this.baseUrl, PATH.DOORS)
  }

  async getAvailableCities(countryId: number, currentLocationId = '') {
    return request<CountriesResponse, ErrorResponse>(
      this.baseUrl,
      addQueryString(PATH.AVAILABLE_CITIES, {
        countryId,
        includeLocationId: currentLocationId,
      })
    )
  }

  async getAllCarTypes() {
    return request<AllCarTypesResponse, ErrorResponse>(
      this.baseUrl,
      PATH.ALL_CAR_TYPES,
      { method: 'GET' }
    )
  }

  async getLocationTypes() {
    return request<ListItemType[], ErrorResponse>(
      this.baseUrl,
      PATH.LOCATION_TYPES,
      { method: 'GET' }
    )
  }
}

export const referencesApi = new Api()
